import * as React from 'react';
import { Component } from 'react';
import { computed, isArrayLike } from 'mobx';
import { observer } from 'mobx-react';
import { inject } from '../utils';
import get from 'lodash/get';
import { performanceCheck as checkPerformance } from '../utils/performanceCheck';
import getDataPoint from '../utils/getDataPoint';
import { DataLocator } from '../models/configuration/DataLocator';
import GetDataPointOptions from '../models/utils/GetDataPointOptions';
import { DataStore, EntityStore } from '../stores';

interface ColumnProps {
    // Injected by mobx
    dataStore?: DataStore;
    entityStore?: EntityStore;
    // Passed Props
    // TODO: check whether this property is deprecated and can possibly be removed
    documentDownloads?: Array<any>;
    // TODO: check whether this property is deprecated and can possibly be removed
    columns?: Array<any>;
    [key: string]: any;
}

// Higher-order component to return a FundCard with data and methods required to function
const withColumns = WrappedComponent => {
    @inject('dataStore', 'entityStore')
    @observer
    class FundCard extends Component<ColumnProps, any> {
        @computed
        get noDataHidden() {
            return this.props.fundFacts.noDataHidden;
        }

        @computed
        get noDataPlaceholder() {
            return (
                this.props.fundFacts.noDataPlaceholder ||
                this.props.dataStore.config.noDataPlaceholder
            );
        }

        // TODO: pull from entity Store?
        @computed
        get hasPerformance() {
            const { dataStore, entityStore } = this.props;
            const { config } = dataStore;
            return checkPerformance({
                dataStore,
                config,
                shareClass: entityStore.value,
            });
        }

        @computed
        get fundFacts() {
            return this.getColumnsWithData(this.props.fundFacts.cards);
        }

        @computed
        get cards() {
            return this.fundFacts.filter(f => f.primary);
        }

        @computed
        get rows() {
            return this.fundFacts.filter(f => !f.primary);
        }

        getColumnsWithData = (columns = []) => {
            let footnoteIndex = -1;

            const values = columns.map((column, index) => {
                const {
                    property,
                    data,
                    disclaimer,
                    asAtDate,
                    noDataHidden,
                    noDataPlaceholder,
                    performanceCheck,
                } = column;

                const columnHasFootnote = disclaimer && disclaimer.footnote;

                if (isArrayLike(data)) {
                    const dataValues = data.map(d => {
                        if (typeof d.label === 'object') {
                            d.label = this.getDataPointValue({ ...d.label });
                        }
                        return {
                            ...d,
                            value: this.getDataPointValue({ ...d }),
                        };
                    });
                    const dataHasValues = !dataValues.some(
                        d => d.value === false
                    );
                    if (dataHasValues && columnHasFootnote) {
                        footnoteIndex++;
                    }

                    return {
                        ...column,
                        multiple: true,
                        data: dataValues,
                        asAtDate: {
                            ...asAtDate,
                            value: asAtDate
                                ? this.getDataPointValue({ ...asAtDate.data })
                                : undefined,
                        },
                        disclaimers: dataHasValues
                            ? this.getDisclaimerContent(
                                  footnoteIndex,
                                  disclaimer
                              )
                            : {},
                    };
                }

                if (data) {
                    if (typeof data.label === 'object') {
                        data.label = this.getDataPointValue({ ...data.label });
                    }
                    const value = this.getDataPointValue({ ...data });
                    if (value && columnHasFootnote) {
                        footnoteIndex++;
                    }

                    return {
                        ...column,
                        data: {
                            ...data,
                            value,
                        },
                        asAtDate: {
                            ...asAtDate,
                            value: asAtDate
                                ? this.getDataPointValue({ ...asAtDate.data })
                                : undefined,
                        },
                        disclaimerConfig: disclaimer,
                        disclaimers: value
                            ? this.getDisclaimerContent(
                                  footnoteIndex,
                                  disclaimer
                              )
                            : {},
                    };
                }

                // Support simple label/property column definition
                if (property) {
                    const value = this.getDataPointValue({
                        property: 'properties_pub',
                        code: property,
                        key: 'value',
                        keyIndex: data && data.keyIndex,
                        noDataHidden,
                        noDataPlaceholder,
                        performanceCheck,
                    });
                    if (value && columnHasFootnote) {
                        footnoteIndex++;
                    }

                    return {
                        ...column,
                        data: {
                            ...data,
                            value,
                        },
                        asAtDate: {
                            ...asAtDate,
                            value: asAtDate
                                ? this.getDataPointValue({ ...asAtDate.data })
                                : undefined,
                        },
                        disclaimers: value
                            ? this.getDisclaimerContent(
                                  footnoteIndex,
                                  disclaimer
                              )
                            : {},
                    };
                }

                return {
                    ...column,
                    data: { value: this.noDataPlaceholder },
                };
            });

            return values;
        };

        getDataPointValue = (
            dataConfig: DataLocator,
            optionsOverride?: GetDataPointOptions
        ): any => {
            const { dataStore, entityStore } = this.props;
            const options: GetDataPointOptions = {
                dataConfig,
                dataStore,
                shareClass: entityStore.value,
                locale: this.locale,
                fundCurrency: this.fundCurrency,
                defaultNoDataPlaceholder: this.noDataPlaceholder,
                noDataHidden: this.noDataHidden,
            };
            return getDataPoint(optionsOverride || options);
        };

        getDisclaimerContent = (index, disclaimer = {} as any) => {
            const { footnote } = disclaimer;
            let character;
            const content = this.props.entityStore.disclaimerByType([
                disclaimer,
            ])[0];

            if (footnote && this.footnoteScheme === 'symbol') {
                const accessor = index % this.footnoteCharacters.length;
                character = get(this.footnoteCharacters, accessor, '*');
            }

            if (footnote && this.footnoteScheme === 'numeric') {
                character = index + 1;
            }

            return {
                character,
                content,
                footnote,
            };
        };

        @computed
        get fundCurrency() {
            return get(
                this.props.entityStore.value,
                `properties_pub.${this.props.dataStore.config
                    .fundCurrencyProperty || 'ccy'}.value`
            );
        }

        @computed
        get locale() {
            return this.props.dataStore.culture;
        }

        @computed
        get footnoteScheme() {
            const {
                footnoteScheme = 'numeric',
            } = this.props.dataStore.config.rowConfig;
            return footnoteScheme;
        }

        @computed
        get footnoteCharacters() {
            const {
                footnoteCharacters = [],
            } = this.props.dataStore.config.rowConfig;
            return footnoteCharacters;
        }

        @computed
        get cardDisclaimers() {
            return this.fundFacts
                .filter(card => card.disclaimers.footnote)
                .map(card => {
                    return card.disclaimers;
                });
        }

        @computed
        get disclaimers() {
            return this.props.entityStore.disclaimerByType(
                this.props.fundFacts.disclaimers
            );
        }

        render() {
            const { cards, rows, disclaimers, cardDisclaimers } = this;

            return (
                <WrappedComponent
                    {...this.props}
                    {...{
                        cards,
                        rows,
                        disclaimers,
                        cardDisclaimers,
                    }}
                />
            );
        }
    }

    return FundCard;
};

export default withColumns;



// WEBPACK FOOTER //
// ./src/Components/withColumns.tsx